import React, { useMemo, useCallback, useReducer } from 'react';

import * as http from '../../http';
import { buildAuthorizedGetRequest } from '../../http/request-templates';
import { PermissionsContext } from './PermissionsContext';
import PermissionsFallbackMessage from './PermissionsFallbackMessage';

import permissionsReducer, {
  ERR_GET_PERMISSIONS,
  permissionsReducerInitialState,
  RCV_GET_PERMISSIONS,
  REQ_GET_PERMISSIONS,
} from './permissionsReducer';
import useAuth from '../Auth/useAuth';
import useSnackbar from '../Snackbar/useSnackbar';

interface ProfileProviderProps {
  children: React.ReactNode;
}

const permissionsBase: string[] = [
  'GraphMaterialsByMonth',
  'GraphWasteHierarchyByMonth',
  'SpreadsheetHazardousWaste',
  'SpreadsheetStatistics',
];

const permissionsEstates: string[] = [
  'GraphEstatesMaterialsByMonth',
  'SpreadsheetEstates',
  'SpreadsheetEstatesStatistics',
];

/**
 * Laddaren i useLoadingState väntar på det här anropet. Ett API som tar emot
 * anslutningen men aldrig svarar får därför inte hålla kvar användaren
 * längre än så här; sedan öppnas portalen med basbehörigheterna.
 */
const PERMISSIONS_TIMEOUT_MS = 10_000;

const PermissionsProvider = ({ children }: ProfileProviderProps) => {
  const { getAccessToken } = useAuth();
  const { pushSnackbar } = useSnackbar();

  const [permissionsState, dispatch] = useReducer(
    permissionsReducer,
    permissionsReducerInitialState
  );

  const getPermissions = useCallback(async () => {
    try {
      dispatch({ type: REQ_GET_PERMISSIONS });
      const permissions = [...permissionsBase];
      const hasEstatesPermissions = await http.rawJson<{ estatesReport: boolean }>(
        `${import.meta.env.VITE_CUSTOMER_PORTAL_API}/estates/permissions`,
        {
          ...buildAuthorizedGetRequest(await getAccessToken()),
          signal: AbortSignal.timeout(PERMISSIONS_TIMEOUT_MS),
        }
      );

      if (hasEstatesPermissions?.estatesReport) {
        permissions.push(...permissionsEstates);
      }

      dispatch({ type: RCV_GET_PERMISSIONS, permissions });
    } catch (e) {
      // Anropet avgör bara om fastighetsrapporterna ska visas. Går det fel
      // får portalen öppna utan dem i stället för att stanna i laddaren,
      // och användaren får veta varför de saknas.
      dispatch({ type: ERR_GET_PERMISSIONS, permissions: [...permissionsBase] });
      pushSnackbar({ type: 'warning', reactNode: <PermissionsFallbackMessage /> });
    }
  }, [getAccessToken, pushSnackbar]);

  const value = useMemo(
    () => ({
      ...permissionsState,
      getPermissions,
    }),
    [permissionsState, getPermissions]
  );

  return <PermissionsContext.Provider value={value}>{children}</PermissionsContext.Provider>;
};

export default PermissionsProvider;
